import { ChevronDown, Download, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { recentTweets, type RecentTweetsResult } from 'sprites-types/workflow/tools/recentTweets'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { TweetItem } from './TweetItem'

interface Props {
  nodeExecution: NodeExecution
}

export function RecentTweetsDisplay({ nodeExecution }: Props) {
  const [isExpanded, setIsExpanded] = useState(true)

  const schema = getToolResultSchema(recentTweets, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle both emitMode: 'single' (array of tweets) and 'multi' (array of arrays of tweets)
  const tweets: RecentTweetsResult = Array.isArray(result[0])
    ? (result as RecentTweetsResult[]).flat()
    : (result as RecentTweetsResult)

  // If there are no tweets, return null to avoid empty spaces
  if (!tweets || tweets.length === 0) {
    return null
  }

  // Header actions (could add export functionality later)
  const headerActions: ContextAction[] = [
    {
      id: 'export-tweets',
      label: 'Export All',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        // Export functionality could be implemented here
        console.log('Export tweets:', tweets)
      },
    },
  ]

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <MessageCircle className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Tweets</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {tweets.length} items
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual tweets - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {tweets.map((tweet) => (
            <TweetItem key={tweet.id} tweet={tweet} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}
